import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { db, FieldInput, type Field } from "./EntityManager";

export const STATUSES = ["Not started", "In progress", "Awaiting review", "Completed", "Not applicable", "Blocked"];
const VERIFICATION = ["Automatically verified", "Human verified", "Manual review required", "Not verified"];

const fields: Field[] = [
  { key: "assignee_name", label: "Assigned person" },
  { key: "reviewer_name", label: "Reviewer" },
  { key: "due_date", label: "Due date", type: "date" },
  { key: "status", label: "Status", type: "select", options: STATUSES },
  { key: "na_reason", label: "Reason (required if Not applicable)", type: "textarea" },
  { key: "findings", label: "Findings", type: "textarea" },
  { key: "evidence_links", label: "Evidence / document links (one per line)", type: "textarea" },
  { key: "follow_up", label: "Follow-up actions", type: "textarea" },
];

export const statusTone = (s: string) =>
  s === "Completed" ? "default" : s === "Blocked" ? "destructive" : s === "Not started" ? "outline" : "secondary";

export const TaskList = ({ orgId, cycleId, canEdit, onChange }: { orgId: string; cycleId: string; canEdit: boolean; onChange?: () => void }) => {
  const [tasks, setTasks] = useState<any[]>([]);
  const [edit, setEdit] = useState<any | null>(null);

  const load = async () => {
    const { data, error } = await db.from("gov_tasks").select("*").eq("org_id", orgId).eq("cycle_id", cycleId).order("category").order("created_at");
    if (error) toast.error(error.message); else setTasks(data ?? []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [cycleId]);

  const save = async () => {
    const payload: any = {};
    [...fields.map((f) => f.key), "verification"].forEach((k) => (payload[k] = edit[k] === "" ? null : edit[k]));
    if (!payload.verification) payload.verification = "Not verified";
    const { error } = await db.from("gov_tasks").update(payload).eq("id", edit.id);
    if (error) return toast.error(error.message);
    toast.success("Task updated");
    setEdit(null); load(); onChange?.();
  };

  return (
    <div className="grid gap-2">
      {tasks.length === 0 && <p className="text-sm text-muted-foreground">No tasks in this cycle.</p>}
      {tasks.map((t) => (
        <button key={t.id} disabled={!canEdit} onClick={() => setEdit(t)}
          className="text-left border border-border rounded-md p-3 bg-card hover:border-accent transition-colors disabled:cursor-default">
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="font-sans font-medium">{t.category} · {t.title}</div>
            <div className="flex gap-1 flex-wrap">
              <Badge variant={statusTone(t.status) as any}>{t.status}</Badge>
              {t.category === "04 Security" && <Badge variant="outline">{t.verification}</Badge>}
            </div>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {t.accountable_role} {t.assignee_name ? `· ${t.assignee_name}` : ""} · due {t.due_date ?? "—"}
            {!t.evidence_links && t.status !== "Not applicable" && <span className="text-destructive"> · evidence missing</span>}
          </div>
          {t.description && <p className="text-sm mt-1 text-foreground/80">{t.description}</p>}
        </button>
      ))}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-sans">{edit?.title}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{edit?.description}</p>
          <div className="space-y-3">
            {fields.map((f) => <FieldInput key={f.key} field={f} value={edit?.[f.key]} onChange={(v) => setEdit((e: any) => ({ ...e, [f.key]: v }))} />)}
            {edit?.category === "04 Security" && (
              <FieldInput field={{ key: "verification", label: "Verification", type: "select", options: VERIFICATION }} value={edit?.verification}
                onChange={(v) => setEdit((e: any) => ({ ...e, verification: v }))} />
            )}
            <p className="text-xs text-muted-foreground">Only a reviewer or admin can mark a task Completed. Approvals are recorded with name and time.</p>
            {edit?.approved_at && <p className="text-xs">Approved {new Date(edit.approved_at).toLocaleString()}</p>}
          </div>
          <DialogFooter><Button variant="accent" onClick={save}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
